import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import districtsTranslations from '../../data/districts.json';
import { addDistrictTranslation, getDistrictTranslation, reloadTranslations, validateTranslationsFile, saveTranslationsToFile } from '../../services/districtsTranslationsService';
import { getAcademyTranslation } from '../../services/academiesTranslationsService';
import { 
  MapPinIcon, 
  SearchIcon, 
  PlusIcon,
  EyeIcon,
  EditIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GlobeIcon,
  GraduationCapIcon,
  BuildingIcon,
  UsersIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon
} from 'lucide-react';

const Districts = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  
  // State management
  const [districts, setDistricts] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [academiesLoading, setAcademiesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const LIMIT_OPTIONS = [10, 20, 50, 100];
  const [academyId, setAcademyId] = useState('');
  
  // Add district modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newDistrict, setNewDistrict] = useState({
    academyId: '',
    code: '',
    translations: {
      fr: '',
      en: '',
      ar: ''
    }
  });
  const [formErrors, setFormErrors] = useState({});

  // Edit district modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState({
    id: null,
    name: '',
    code: '',
    academyId: ''
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  // Fetch academies for filter
  const fetchAcademies = async () => {
    try {
      setAcademiesLoading(true);
      console.log('Fetching academies...');
      
      const response = await api.get('/organization/academies?page=1&limit=100');
      console.log('Academies API response:', response.data);
      
      if (response.data.success) {
        console.log('Academies data:', response.data.data.academies);
        setAcademies(response.data.data.academies);
      } else {
        console.error('API returned success: false', response.data);
        setAcademies([]);
      }
    } catch (error) {
      console.error('Error fetching academies:', error);
      setAcademies([]);
      toast.error(t('districts.academiesFetchError'), { duration: 5000, position: 'top-center' });
    } finally {
      setAcademiesLoading(false);
    }
  };

  // Fetch districts - API: /districts or /academies/{id}/districts
  const fetchDistricts = async (page = 1, search = '', filterAcademyId = academyId, itemsPerPage = limit) => {
    try {
      setLoading(true);
      let url = '/organization/districts';
      if (filterAcademyId) url = `/organization/academies/${filterAcademyId}/districts`;
      const params = new URLSearchParams({ page: page.toString(), limit: itemsPerPage.toString() });
      if (search.trim()) params.set('search', search.trim());
      const response = await api.get(`${url}?${params.toString()}`);
      console.log('Districts API response:', response.data);
      
      if (response.data.success) {
        setDistricts(response.data.data.districts);
        setTotal(response.data.data.pagination.total);
        setCurrentPage(response.data.data.pagination.page);
      } else {
        throw new Error(response.data.message || t('districts.fetchError'));
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      toast.error(t('districts.fetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAcademies(); }, []);

  useEffect(() => {
    if (searchTerm === '') {
      fetchDistricts(1, '', academyId);
      return;
    }
    const timeoutId = setTimeout(() => {
      setIsSearching(true);
      fetchDistricts(1, searchTerm, academyId).finally(() => setIsSearching(false));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, academyId]);

  const handleSearch = (e) => {
    e?.preventDefault?.();
    setCurrentPage(1);
    fetchDistricts(1, searchTerm, academyId);
  };

  const clearFilters = () => {
    setAcademyId('');
    setSearchTerm('');
    setCurrentPage(1);
    fetchDistricts(1, '', '', limit);
  };

  const getActiveFiltersCount = () => (academyId ? 1 : 0);

  const didMountDistricts = useRef(false);
  useEffect(() => {
    if (!didMountDistricts.current) { didMountDistricts.current = true; return; }
    setCurrentPage(1);
    fetchDistricts(1, searchTerm, academyId, limit);
  }, [academyId, limit]);

  // Add district
  const addDistrict = async () => {
    const errors = {};
    if (!newDistrict.academyId) {
      errors.academyId = t('districts.academyRequired');
    }
    if (!newDistrict.code.trim()) {
      errors.code = t('districts.codeRequired');
    } else if (newDistrict.code.trim().length < 2 || newDistrict.code.trim().length > 10) {
      errors.code = t('districts.codeLength');
    }
    if (!newDistrict.translations.fr.trim()) {
      errors.translation_fr = t('districts.nameRequired_fr');
    }
    if (!newDistrict.translations.en.trim()) {
      errors.translation_en = t('districts.nameRequired_en');
    }
    if (!newDistrict.translations.ar.trim()) {
      errors.translation_ar = t('districts.nameRequired_ar');
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsAdding(true);
    try {
      const requestData = {
        academyId: parseInt(newDistrict.academyId),
        name: newDistrict.translations.ar.trim(), // Use Arabic name as primary name
        code: newDistrict.code.trim()
      };
      
      const response = await api.post('/organization/districts', requestData);
      
      if (response.data.success) {
        console.log('District created successfully with ID:', response.data.data?.districtId);
        
        // Save translations using the new system
        try {
          const translationResult = addDistrictTranslation(
            newDistrict.translations.ar.trim(),
            {
              fr: newDistrict.translations.fr.trim(),
              en: newDistrict.translations.en.trim(),
              ar: newDistrict.translations.ar.trim()
            }
          );

          if (translationResult.success) {
            console.log('Translation saved successfully:', translationResult.message);
            toast.success(response.data.message || t('districts.addSuccess'), { duration: 3000, position: 'top-center' });
          } else {
            toast.success(response.data.message || t('districts.addSuccess'), { duration: 3000, position: 'top-center' });
            console.warn('District added but translation save failed:', translationResult.message);
          }
          
          setNewDistrict({ academyId: '', code: '', translations: { fr: '', en: '', ar: '' } });
          setFormErrors({});
          setShowAddModal(false);
          fetchDistricts(currentPage, searchTerm, academyId);
        } catch (translationError) {
          console.error('Error saving district translations:', translationError);
          toast.error(t('districts.addError'), { duration: 3000, position: 'top-center' });
        }
      } else {
        throw new Error(response.data.message || t('districts.addError'));
      }
    } catch (error) {
      console.error('Error adding district:', error);
      const errorMessage = error.response?.data?.message || error.message || t('districts.addError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsAdding(false);
    }
  };


  // Translate district name
  const translateDistrictName = (arabicName, language) => {
    return getDistrictTranslation(arabicName, language);
  };

  // Translate academy name
  const translateAcademyName = (arabicName, language) => {
    return getAcademyTranslation(arabicName, language);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    setNewDistrict(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle translation change
  const handleTranslationChange = (language, value) => {
    setNewDistrict(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [language]: value
      }
    }));
    
    // Clear error when user starts typing
    const errorKey = `translation_${language}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };


  // Handle status toggle
  const handleStatusToggle = async (districtId, currentStatus) => {
    try {
      const response = await api.put(`/districts/${districtId}/toggle-status`, {
        is_active: currentStatus === 1 ? 0 : 1
      });
      
      if (response.data.success) {
        toast.success(t('districts.statusUpdated'), { duration: 3000, position: 'top-center' });
        fetchDistricts(currentPage, searchTerm, academyId);
      } else {
        throw new Error(response.data.message || t('districts.statusUpdateError'));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(t('districts.statusUpdateError'), { duration: 3000, position: 'top-center' });
    }
  };

  // Handle edit district
  const handleEditDistrict = (district) => {
    setEditingDistrict({
      id: district.id,
      name: district.name,
      code: district.code,
      academyId: district.academy_id || ''
    });
    setEditFormErrors({});
    setShowEditModal(true);
  };

  // Update district
  const updateDistrict = async () => {
    try {
      setIsEditing(true);
      setEditFormErrors({});

      // Validation
      const errors = {};
      if (!editingDistrict.name.trim()) {
        errors.name = t('districts.nameRequired');
      }
      if (!editingDistrict.code.trim()) {
        errors.code = t('districts.codeRequired');
      }
      if (editingDistrict.code.trim().length < 2 || editingDistrict.code.trim().length > 10) {
        errors.code = t('districts.codeLength');
      }
      if (!editingDistrict.academyId) {
        errors.academyId = t('districts.academyRequired');
      }

      if (Object.keys(errors).length > 0) {
        setEditFormErrors(errors);
        return;
      }

      const response = await api.put(`/organization/districts/${editingDistrict.id}`, {
        name: editingDistrict.name.trim(),
        code: editingDistrict.code.trim(),
        academyId: parseInt(editingDistrict.academyId)
      });

      if (response.data.success) {
        toast.success(t('districts.updateSuccess'));
        setShowEditModal(false);
        setEditingDistrict({ id: null, name: '', code: '', academyId: '' });
        fetchDistricts(currentPage, searchTerm, academyId);
      } else {
        throw new Error(response.data.message || t('districts.updateError'));
      }
    } catch (error) {
      console.error('Error updating district:', error);
      const errorMessage = error.response?.data?.message || error.message || t('districts.updateError');
      toast.error(errorMessage);
    } finally {
      setIsEditing(false);
    }
  };

  // Delete district
  const deleteDistrict = async (districtId, districtName) => {
    try {
      const confirmed = window.confirm(
        `${t('districts.deleteConfirm')} "${districtName}"?`
      );
      
      if (!confirmed) {
        return;
      }

      const response = await api.delete(`/organization/districts/${districtId}`);
      
      if (response.data.success) {
        toast.success(t('districts.deleteSuccess'));
        fetchDistricts(currentPage, searchTerm, academyId);
      } else {
        throw new Error(response.data.message || t('districts.deleteError'));
      }
    } catch (error) {
      console.error('Error deleting district:', error);
      const errorMessage = error.response?.data?.message || error.message || t('districts.deleteError');
      toast.error(errorMessage);
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchDistricts(newPage, searchTerm, academyId);
  };

  // Modal handlers
  const openAddModal = () => {
    setShowAddModal(true);
    setNewDistrict({ academyId: '', code: '', translations: { fr: '', en: '', ar: '' } });
    setFormErrors({});
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setNewDistrict({ academyId: '', code: '', translations: { fr: '', en: '', ar: '' } });
    setFormErrors({});
  };

  // Export translations
  const handleExportTranslations = () => {
    try {
      const result = saveTranslationsToFile();
      if (result.success) {
        toast.success(result.message, { duration: 3000, position: 'top-center' });
      } else {
        toast.error(result.message, { duration: 3000, position: 'top-center' });
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('districts.exportError'), { duration: 3000, position: 'top-center' });
    }
  };

  return (
    <div className={`p-6 ${isRTL ? 'font-arabic' : ''}`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <MapPinIcon className={`h-8 w-8 text-primary-600 ${isRTL ? 'ml-3' : 'mr-3'}`} />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('districts.title')}
              </h1>
              <p className="text-gray-600 mt-1">
                {t('districts.subtitle')}
              </p>
            </div>
          </div>
          <div className={`flex ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <button 
              onClick={openAddModal}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
            >
              <PlusIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('districts.addDistrict')}
            </button>
            <button 
              onClick={handleExportTranslations}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <GlobeIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('districts.exportTranslations')}
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters - Modern Design */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <form onSubmit={handleSearch} className="p-4">
          <div className={`flex flex-col lg:flex-row gap-4 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('common.search')}</label>
              <div className="relative">
                <SearchIcon className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                <input type="text" placeholder={t('districts.searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors`} />
                {isSearching && <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}><div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent" /></div>}
              </div>
            </div>
            <div className="lg:w-48">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('districts.academy')}</label>
              <select value={academyId} onChange={(e) => setAcademyId(e.target.value)} disabled={academiesLoading} className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors">
                <option value="">{academiesLoading ? t('districts.loadingAcademies') : t('districts.allAcademies')}</option>
                {academies.map((academy) => <option key={academy.id} value={academy.id}>{translateAcademyName(academy.name, currentLanguage || 'fr')}</option>)}
              </select>
            </div>
            <div className="lg:w-32">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('users.perPage')}</label>
              <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))} className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors">
                {LIMIT_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2 lg:flex-shrink-0">
              <button type="submit" className="px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors">{t('common.search')}</button>
              {(academyId || searchTerm) && (
                <button type="button" onClick={clearFilters} title={t('common.clearFilters')} className="p-2.5 rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"><XCircleIcon className="h-5 w-5" /></button>
              )}
            </div>
          </div>
        </form>
      </div>


      {/* Districts Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('districts.name')}
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('districts.code')}
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('districts.academy')}
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('districts.status')}
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('districts.createdAt')}
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('districts.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      <span className="ml-2 text-gray-600">{t('common.loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : districts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    {t('districts.noDistricts')}
                  </td>
                </tr>
              ) : (
                districts.map((district) => (
                  <tr key={district.id} className="hover:bg-gray-50">
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <MapPinIcon className="h-4 w-4 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4 text-left">
                          <div className="text-sm font-medium text-gray-900">
                            {translateDistrictName(district.name, currentLanguage || 'fr')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <BuildingIcon className="h-3 w-3 mr-1" />
                        {district.code}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="text-sm text-gray-900">
                        {translateAcademyName(district.academy_name, currentLanguage || 'fr')}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <button
                        onClick={() => handleStatusToggle(district.id, district.is_active)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          district.is_active === 1
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        } transition-colors cursor-pointer`}
                      >
                        {district.is_active === 1 ? (
                          <>
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            {t('districts.active')}
                          </>
                        ) : (
                          <>
                            <XCircleIcon className="h-3 w-3 mr-1" />
                            {t('districts.inactive')}
                          </>
                        )}
                      </button>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {formatDate(district.created_at)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="flex items-center justify-start space-x-2">
                        <button className="text-primary-600 hover:text-primary-900 p-1" title={t('districts.view')}>
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleEditDistrict(district)}
                          className="text-blue-600 hover:text-blue-900 p-1" 
                          title={t('districts.edit')}
                        >
                          <EditIcon className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => deleteDistrict(district.id, district.name)}
                          className="text-red-600 hover:text-red-900 p-1" 
                          title={t('districts.delete')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('districts.previous')}
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage * limit >= total}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('districts.next')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  {t('districts.showing')} <span className="font-medium">{((currentPage - 1) * limit) + 1}</span> {t('districts.to')} <span className="font-medium">{Math.min(currentPage * limit, total)}</span> {t('districts.of')} <span className="font-medium">{total}</span> {t('districts.results')}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.ceil(total / limit) }, (_, i) => i + 1).map((page) => {
                    // Show first page, last page, current page, and pages around current page
                    const shouldShow = 
                      page === 1 || 
                      page === Math.ceil(total / limit) || 
                      Math.abs(page - currentPage) <= 1;
                    
                    if (!shouldShow) {
                      // Show ellipsis for gaps
                      if (page === 2 && currentPage > 4) {
                        return (
                          <span key={`ellipsis-${page}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        );
                      }
                      if (page === Math.ceil(total / limit) - 1 && currentPage < Math.ceil(total / limit) - 3) {
                        return (
                          <span key={`ellipsis-${page}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        );
                      }
                      return null;
                    }
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage * limit >= total}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add District Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : 'flex-row'} mb-4`}>
              <h3 className="text-lg font-medium text-gray-900">
                {t('districts.addDistrict')}
              </h3>
              <button
                onClick={closeAddModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); addDistrict(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('districts.academy')} *
                </label>
                <select
                  value={newDistrict.academyId}
                  onChange={(e) => handleInputChange('academyId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.academyId ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={academiesLoading}
                >
                  <option value="">
                    {academiesLoading ? t('districts.loadingAcademies') : t('districts.selectAcademy')}
                  </option>
                  {academies.map((academy) => (
                    <option key={academy.id} value={academy.id}>
                      {translateAcademyName(academy.name, currentLanguage || 'fr')}
                    </option>
                  ))}
                </select>
                {formErrors.academyId && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.academyId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('districts.code')} *
                </label>
                <input
                  type="text"
                  value={newDistrict.code}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.code ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Ex: DIS01"
                  maxLength={10}
                />
                {formErrors.code && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.code}</p>
                )}
              </div>

              {/* Translation Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* French Translation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🇫🇷 Français *
                  </label>
                  <input
                    type="text"
                    value={newDistrict.translations.fr}
                    onChange={(e) => handleTranslationChange('fr', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.translation_fr ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: District d'Abu Dhabi"
                  />
                  {formErrors.translation_fr && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.translation_fr}</p>
                  )}
                </div>

                {/* English Translation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🇺🇸 English *
                  </label>
                  <input
                    type="text"
                    value={newDistrict.translations.en}
                    onChange={(e) => handleTranslationChange('en', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.translation_en ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Abu Dhabi District"
                  />
                  {formErrors.translation_en && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.translation_en}</p>
                  )}
                </div>

                {/* Arabic Translation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🇸🇦 العربية *
                  </label>
                  <input
                    type="text"
                    value={newDistrict.translations.ar}
                    onChange={(e) => handleTranslationChange('ar', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.translation_ar ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: إقليم أبوظبي"
                  />
                  {formErrors.translation_ar && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.translation_ar}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAddModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isAdding}
                  className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isAdding && (
                    <div className={`animate-spin rounded-full h-4 w-4 border-b-2 border-white ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                  )}
                  {isAdding ? t('districts.adding') : t('districts.addDistrict')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit District Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('districts.editDistrict')}
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); updateDistrict(); }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('districts.districtName')} *
                  </label>
                  <input
                    type="text"
                    value={editingDistrict.name}
                    onChange={(e) => setEditingDistrict(prev => ({ ...prev, name: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      editFormErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('districts.districtName')}
                  />
                  {editFormErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{editFormErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('districts.districtCode')} *
                  </label>
                  <input
                    type="text"
                    value={editingDistrict.code}
                    onChange={(e) => setEditingDistrict(prev => ({ ...prev, code: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      editFormErrors.code ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('districts.districtCode')}
                    maxLength={10}
                  />
                  {editFormErrors.code && (
                    <p className="mt-1 text-sm text-red-600">{editFormErrors.code}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('districts.academy')} *
                  </label>
                  <select
                    value={editingDistrict.academyId}
                    onChange={(e) => setEditingDistrict(prev => ({ ...prev, academyId: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      editFormErrors.academyId ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={academiesLoading}
                  >
                    <option value="">
                      {academiesLoading ? t('districts.loadingAcademies') : t('districts.selectAcademy')}
                    </option>
                    {academies.map((academy) => (
                      <option key={academy.id} value={academy.id}>
                        {translateAcademyName(academy.name, currentLanguage || 'fr')}
                      </option>
                    ))}
                  </select>
                  {editFormErrors.academyId && (
                    <p className="mt-1 text-sm text-red-600">{editFormErrors.academyId}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isEditing}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  {isEditing ? t('districts.updating') : t('districts.update')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Districts;
